import { useMemo } from "react";
import MiniSearch, { Suggestion } from "minisearch";
import { IStacCollection } from "types/stac";
import { mediaTypeOverride } from "utils/stac";

export const useCollectionFilter = (collections: IStacCollection[]) => {
  const searchIndex = useMemo(() => {
    const searchIndex = new MiniSearch({
      fields: [
        "id",
        "title",
        "msft:short_description",
        "keywords",
        "eo:bands",
        "variables",
        "mediaType",
        "providers",
      ],
      searchOptions: {
        boost: {
          id: 5,
          title: 3,
          "msft:short_description": 2,
          keywords: 2,
        },
      },
      extractField: searchIndexerFields,
    });
    searchIndex.addAll(collections);
    return searchIndex;
  }, [collections]);

  return {
    search: (filterText: string) => search(filterText, searchIndex, collections),
    suggest: (filterText: string): Suggestion =>
      searchIndex.autoSuggest(filterText, { ...searchOpts, fuzzy: true })?.[0] ||
      null,
  };
};

const search = (
  filterText: string,
  filterIndex: MiniSearch<IStacCollection>,
  collections: IStacCollection[]
): IStacCollection[] => {
  let filteredCollections = collections;
  if (filterText) {
    const searchResults = filterIndex.search(filterText, searchOpts);

    const matchedIds = searchResults.map(r => r.id);
    filteredCollections = matchedIds
      .map(id => collections.find(c => c.id === id))
      .filter(Boolean) as IStacCollection[];
  }

  return filteredCollections;
};

const searchOpts = {
  prefix: true,
  combineWith: "AND",
  weights: { prefix: 0.8, fuzzy: 0.8 },
};

const searchIndexerFields = (collection: IStacCollection, fieldName: string) => {
  switch (fieldName) {
    case "keywords":
      return collection.keywords?.join(" ") || "";
    case "eo:bands":
      return (
        collection.summaries?.["eo:bands"]?.map(
          (eoband: any) => eoband?.common_name
        ) || ""
      );
    case "variables":
      return Object.values(collection?.["cube:variables"] || {})
        .map(v => v?.attrs?.standard_name)
        .join(" ");
    case "mediaType":
      const itemAssets = Object.values(collection.item_assets || {})
        .map(asset => mediaTypeOverride(asset.type))
        .join(" ");
      const collectionAssets = Object.values(collection.assets || {})
        .map(asset => mediaTypeOverride(asset.type))
        .join(" ");
      return `${itemAssets} ${collectionAssets}`;
    case "providers":
      return collection.providers?.map(p => p.name).join(" ");
  }

  return fieldName.split(".").reduce((doc: any, key) => doc && doc[key], collection);
};
